import { Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { EnzymeState } from './state/enzyme.state';
import { EnzymeVault } from '../model';
import {
  persistState,
  sessionStorageStrategy,
} from '@ngneat/elf-persist-state';
import { plainToClass } from 'class-transformer';
import { Chain, ChainRepository, SignerRepository } from 'common';
import { Address, AddressUtil } from '@31third/common';
import { RebalancingRepository } from './rebalancing.repository';
import { SettingsRepository } from './settings.repository';
import { EnzymeVaultFactory } from '../factory';

const INITIAL_ENZYME_STATE = {
  connectedSigner: undefined,
  chain: undefined,
  vaults: [],
  vaultsLoaded: false,
  selectedVault: undefined,
  useOwnerInsteadOfConnectedWallet: false,
  isTaurus: false,
};

const store = createStore(
  { name: 'enzyme' },
  withProps<EnzymeState>(INITIAL_ENZYME_STATE),
);

export const persistEnzyme = persistState(store, {
  key: 'enzyme',
  storage: sessionStorageStrategy,
  preStoreInit: state => {
    // initialize objects
    return plainToClass(EnzymeState, state);
  },
});

@Injectable({ providedIn: 'root' })
export class EnzymeRepository {
  private static readonly TAURUS_ADDRESS =
    '0x4819ac09e4619748b1cdf657283a948731fa6ab6';

  constructor(
    private readonly chainRepository: ChainRepository,
    private readonly signerRepository: SignerRepository,
    private readonly rebalancingRepository: RebalancingRepository,
    private readonly settingsRepository: SettingsRepository,
  ) {
    this.subscribeToChainChanges();
    this.subscribeToSettingsChanges();
  }

  vaultsLoaded$ = store.pipe(select(({ vaultsLoaded }) => vaultsLoaded));
  selectedVault$ = store.pipe(select(({ selectedVault }) => selectedVault));
  isTaurus$ = store.pipe(select(({ isTaurus }) => isTaurus));

  private subscribeToChainChanges(): void {
    this.chainRepository.chain$.subscribe(chain => {
      if (chain?.identifier !== store.getValue().chain?.identifier) {
        this.setChain(chain);
        this.setVaults([], false);
      }
    });
  }

  private subscribeToSettingsChanges(): void {
    this.settingsRepository.enzymeVaultChanged$.subscribe(vaultAddress => {
      const vaults = store.getValue().vaults;
      if (
        !vaults.find(
          vault =>
            vaultAddress && AddressUtil.equals(vault.address, vaultAddress),
        )
      ) {
        // remove current settings vault
        const settingsVaultIndex = vaults.findIndex(
          vault => vault.isSettingsVault,
        );
        if (settingsVaultIndex >= 0) {
          vaults.splice(settingsVaultIndex, 1);
        }

        if (vaultAddress) {
          vaults.push(
            EnzymeVaultFactory.createPlaceholder(
              vaultAddress,
              this.chainRepository.store.getValue().chain,
              true,
            ),
          );
        }

        this.setVaults(vaults, true);
      }
    });
  }

  public get store() {
    return store;
  }

  public setChain(chain: Chain | undefined): void {
    store.next({
      ...store.getValue(),
      chain,
    });
  }

  public setVaults(vaults: EnzymeVault[], vaultsLoaded: boolean): void {
    store.next({
      ...store.getValue(),
      connectedSigner: this.getConnectedSigner(),
      vaults,
      vaultsLoaded,
    });

    if (!this.isSelectedInVaults()) {
      this.resetSelectedVault();
    }
  }

  private getConnectedSigner(): Address | undefined {
    return this.signerRepository.store.getValue().address;
  }

  private isSelectedInVaults(): boolean {
    const selected = store.getValue().selectedVault;
    return (
      store
        .getValue()
        .vaults.find(vault => vault.address === selected?.address) !== undefined
    );
  }

  public setSelectedVault(vault: EnzymeVault): void {
    store.next({
      ...store.getValue(),
      selectedVault: vault,
      useOwnerInsteadOfConnectedWallet: !vault.ownedByConnectedSigner,
      isTaurus: AddressUtil.equals(
        vault.address,
        EnzymeRepository.TAURUS_ADDRESS,
      ),
    });
  }

  public resetSelectedVault(): void {
    if (store.getValue().selectedVault) {
      store.next({ ...store.getValue(), selectedVault: undefined });
      this.rebalancingRepository.reset();
    }
  }

  public setUseOwnerInsteadOfConnectedWallet(
    useOwnerInsteadOfConnectedWallet: boolean,
  ): void {
    store.next({ ...store.getValue(), useOwnerInsteadOfConnectedWallet });
  }
}
